import { useEffect, useState } from "react";

/**
 * Returns the class name matching the component's mount state: the `false`
 * entry for the first committed state, then the `true` entry from the frame
 * after mount onwards.
 *
 * Lets a component opt out of behavior — most commonly transitions — for its
 * initial render, including when a parent resolves the relevant prop in a
 * layout effect and flips it before the first paint. Because the state flips
 * after the first frame, only changes that happen after mount take effect.
 *
 * @param cls Class names keyed by mount state; omit a key to apply none.
 */
export const useMountStateCls = (cls: {
  /** Applied for the first committed state, before the frame after mount. */
  false?: string;
  /** Applied from the frame after mount onwards. */
  true?: string;
}): string | undefined => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMounted(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);

  return mounted ? cls.true : cls.false;
};
